'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { Map as ImmutableMap } from 'immutable';
import debounce from 'transmute/debounce';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import AutofillHoneypot from './component/AutofillHoneypot';
import { Form } from 'foundations-components/transitional/components/Form';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import InboxFormActions from './section/InboxFormActions';
import ExchangeServerSettings from './section/ExchangeServerSettings';
import { getServerValidationMessages, isValidEmail } from 'InboxConnectUtils/utils/Validation';
import { isNonEmptyString } from '../../utils/StringValidation';
import { isExchangeAddress } from '../../utils/Validation';
import { filterFalseyValuesFromObject } from '../../utils/ObjectUtils';
import ExchangeAccountSettings from './section/ExchangeAccountSettings';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LANG_ROUTE = 'InboxConnectUtils.genericInboxForm';
const isValidPassword = isNonEmptyString;

/**
 * @internal
 */
export default class ExchangeInboxForm extends PureComponent {
  constructor(props) {
    super(props);
    this.state = Object.assign({}, this.getValidationState(this.props.value));
    this.getIsSubmittable = this.getIsSubmittable.bind(this);
    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.validateInput = debounce(500, this.validateInput.bind(this));
  }
  componentWillUnmount() {
    this.validateInput.cancel();
  }
  componentDidUpdate(prevProps) {
    if (this.props.value.email !== prevProps.value.email || this.props.value.password !== prevProps.value.password || this.props.value.exchangeUrl !== prevProps.value.exchangeUrl) {
      this.setState(Object.assign({}, this.getValidationState(this.props.value)));
    }
  }
  getIsSubmittable() {
    const {
      disabled,
      isLoading,
      serverValidationErrors,
      value
    } = this.props;
    return !disabled && !isLoading && this.state.isEmailValid && (!this.props.didAutoDiscoveryFail && this.state.isExchangeUrlEmpty || this.state.isExchangeUrlValid) && isValidPassword(value.password) && (serverValidationErrors.get('UNTRUSTED_CERTIFICATES') ? value.acceptUntrustedCert === true : true);
  }
  getValidationState(value) {
    const isEmailValid = value.email ? isValidEmail(value.email) : null;
    const isExchangeUrlEmpty = !(value.exchangeUrl && value.exchangeUrl.length > 0);
    const isExchangeUrlValid = value.exchangeUrl ? isExchangeAddress(value.exchangeUrl) : null;
    return {
      isEmailValid,
      isExchangeUrlEmpty,
      isExchangeUrlValid,
      mostRecentlyValidatedEmail: value.email || ''
    };
  }
  validateInput(value) {
    this.setState(this.getValidationState(value));
  }
  handleFieldChange({
    target: {
      value: fieldValue
    }
  }, fieldName) {
    const {
      onChange,
      value
    } = this.props;
    const nextValue = value.set(fieldName, fieldValue);
    onChange(syntheticEvent(nextValue));
    this.validateInput(nextValue);
  }
  render() {
    const {
      cancelText,
      isEmailFieldDisabled,
      didExpandAdvancedSettings,
      didAutoDiscoveryFail,
      disabled,
      isLoading,
      handleExpand,
      onCancel,
      onSubmit,
      serverValidationErrors,
      submitText,
      value
    } = this.props;
    const {
      isEmailValid,
      isExchangeUrlValid
    } = this.state;
    const isExpanded = didExpandAdvancedSettings || didAutoDiscoveryFail;
    const isSubmittable = this.getIsSubmittable();
    const clientValidationMessages = {
      email: isEmailValid !== null && (isEmailValid ? undefined : `${LANG_ROUTE}.emailInvalid`),
      exchangeUrlError: isExchangeUrlValid === false
    };
    const validationMessages = filterFalseyValuesFromObject(Object.assign({}, getServerValidationMessages(serverValidationErrors), clientValidationMessages));
    return /*#__PURE__*/_jsxs(Form, {
      action: "",
      textAlign: "start",
      marginBlockStart: "400",
      autoComplete: "off",
      onSubmit: !disabled && isSubmittable ? onSubmit : emptyFunction,
      children: [/*#__PURE__*/_jsx(ExchangeAccountSettings, {
        onFieldChange: this.handleFieldChange,
        isEmailFieldDisabled: isEmailFieldDisabled,
        isUsernameFieldHidden: !isExpanded,
        validationMessages: validationMessages,
        value: value
      }), isExpanded && /*#__PURE__*/_jsx(ExchangeServerSettings, {
        isRequired: didAutoDiscoveryFail,
        validationMessages: validationMessages,
        value: value,
        disabled: disabled,
        handleFieldChange: this.handleFieldChange
      }), /*#__PURE__*/_jsx(AutofillHoneypot, {}), !isExpanded && /*#__PURE__*/_jsx(Link, {
        marginBlockStart: "500",
        display: "block",
        textAlign: "center",
        color: "text-tertiary-default",
        onClick: () => {
          handleExpand();
        },
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: `${LANG_ROUTE}.exchange.showOptionalFields`
        })
      }), isLoading && /*#__PURE__*/_jsx(LoadingSpinner, {
        marginBlockStart: "1000",
        grow: true
      }), /*#__PURE__*/_jsx(InboxFormActions, {
        onSubmit: onSubmit,
        onCancel: onCancel,
        submitText: submitText,
        cancelText: cancelText,
        isSubmittable: isSubmittable
      })]
    });
  }
}
ExchangeInboxForm.defaultProps = {
  isEmailFieldDisabled: false,
  isLoading: false,
  serverValidationErrors: ImmutableMap()
};
ExchangeInboxForm.propTypes = {
  cancelText: PropTypes.node,
  disabled: PropTypes.bool,
  isEmailFieldDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  didAutoDiscoveryFail: PropTypes.bool.isRequired,
  didExpandAdvancedSettings: PropTypes.bool.isRequired,
  handleExpand: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  serverValidationErrors: PropTypes.instanceOf(ImmutableMap).isRequired,
  submitText: PropTypes.node,
  value: PropTypes.object.isRequired
};